import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { debounce } from "lodash";
import {
  Box,
  TextField,
  ListItem,
  ListItemButton,
  Checkbox,
  ListItemText,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useContext } from "react";
import { PlatformContext } from "./AddPromotions";
import { getPlatformCategory } from "../../redux/slices/promotions";

/**
 * CategoryList component fetches and displays a list of categories
 *
 * @param {Function} changeHandler - Callback function to handle category selection.
 * @param {Object} inputValue - Object/Array containing the currently selected category details.
 */
export default function CategoryList({
  changeHandler = () => {},
  inputValue = {},
  isShowAnyOption = false,
  listType = "",
  setAnchorEl,
}) {
  const dispatch = useDispatch();
  const [filter, setFilter] = useState({
    skip: 0,
    limit: 10,
    searchTerm: "",
  });
  const platform = useContext(PlatformContext);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const [categoriesList, setCategoriesList] = useState([]);
  const [selectAllActive, setSelectAllActive] = useState(false);

  const observer = useRef();

  const lastPostElementRef = useCallback(
    (node) => {
      if (isLoading || categoriesList.length >= total) return;
      if (observer.current) observer.current.disconnect();
      const { skip, limit, searchTerm } = filter;

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            const newSkip = skip + limit;
            setIsLoading(true);

            dispatch(
              getPlatformCategory({
                skip: newSkip,
                limit,
                platform: platform,
                searchTerm: searchTerm || "",
              })
            )
              .unwrap()
              .then((res) => {
                const { data, total: newTotal } = res;
                const newCategories = data?.map((item) => ({
                    categoryId: item?.categoryId,
                    categoryName: item?.categoryName,
                    categorySlug: item?.categorySlug,
                    status: item?.status,
                }));
                setCategoriesList((prev) => [...prev, ...newCategories]);
                setTotal(newTotal);
                setFilter((prev) => ({
                  ...prev,
                  skip: newSkip,
                }));
                 if(isAllSelected)
                handleSelectAll()
              })
              .finally(() => setIsLoading(false));
          }
        },
        {
          root: null,
          rootMargin: "100px",
          threshold: 0.1,
        }
      );

      if (node) observer.current.observe(node);
    },
    [isLoading, categoriesList.length, total, filter, platform, dispatch]
  );

  useEffect(() => {
    setIsLoading(true);
    dispatch(
      getPlatformCategory({
        skip: filter?.skip,
        limit: filter?.limit,
        platform: platform,
        searchTerm: filter?.searchTerm || "",
      })
    )
      .unwrap()
      .then((res) => {
        setIsLoading(false);

        const { data, total } = res;
        const categoryData = data?.map((item) => {
          return {
            categoryId: item?.categoryId,
            categoryName: item?.categoryName,
            categorySlug: item?.categorySlug,
            status: item?.status,
            status: item?.status,
          };
        });
        setTotal(total);
        setCategoriesList(categoryData);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [platform]);

    useEffect(() => {
    if (!selectAllActive || !Array.isArray(inputValue)) return;
  
    const newSelections = categoriesList.filter(
      (category) =>
        !inputValue.some(
          (item) => item.categoryId === category.categoryId
        )
    );
  
    if (newSelections.length > 0) {
      changeHandler({
        ruleValue: [...inputValue, ...newSelections],
      });
    }
  }, [categoriesList.length]);

  const isAllSelected =
    Array.isArray(inputValue) &&
    categoriesList.length > 0 &&
    categoriesList.every((category) =>
      inputValue.some((item) => item.categoryId === category.categoryId)
    );

  const handleSelectAll = () => {
    if (!Array.isArray(inputValue)) return;

    if (selectAllActive) {
      // Clear only loaded categories
      const remaining = inputValue.filter(
        (item) =>
          !categoriesList.some(
            (category) => category.categoryId === item.categoryId
          )
      );

      changeHandler({ ruleValue: remaining });
      setSelectAllActive(false);
    } else {
        // Select logic
      const merged = [
        ...inputValue,
        ...categoriesList.filter(
          (category) =>
            !inputValue.some(
              (item) => item.categoryId === category.categoryId
            )
        ),
      ];

      changeHandler({ ruleValue: merged });
      setSelectAllActive(true);
    }
  };


  const Row = ({ index, style }) => {
    const category = categoriesList[index];

    const isChecked = Array.isArray(inputValue)
      ? inputValue.some((item) => item?.categoryId === category?.categoryId)
      : inputValue?.categoryId === category?.categoryId;

    return (
      <ListItem
        key={category?.categoryId}
        disablePadding
        style={style}
        ref={categoriesList.length === index + 1 ? lastPostElementRef : null}
      >
        <ListItemButton
          onClick={() => {
            if (Array.isArray(inputValue)) {
              if (!isChecked) {
                changeHandler({ ruleValue: [...inputValue, category] });
              } else {
                changeHandler({
                  ruleValue: inputValue.filter((item) => item?.categoryId !== category?.categoryId),
                });
              }
            } else {
              setAnchorEl(null);
              changeHandler({
                ruleValue: isChecked ? inputValue : category,
              });
            }
          }}
          sx={{
            p: 0,
            bgcolor: (_theme) => (isChecked ? "action.selected" : "inherit"),
          }}
          className="popover-btn-area bg-transparent"
        >
          {(Array.isArray(inputValue)) && (
            <Checkbox
              edge="start"
              checked={isChecked}
              tabIndex={-1}
              disableRipple
              onClick={(e) => {
                e.stopPropagation();
                if (e.target.checked) {
                  if (!isChecked) {
                    changeHandler({ ruleValue: [...inputValue, category] });
                  }
                } else {
                  changeHandler({
                    ruleValue: inputValue.filter((item) => item?.categoryId !== category?.categoryId),
                  });
                }
              }}
              style={{ margin: 0 }}
            />
          )}

          <ListItemText
            primary={
                <Typography variant="body2" color="#2E2E3A" fontWeight="semibold">
                  {category.categoryName}
                </Typography>
            }
          />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      {/* Search Box */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search categories..."
          onChange={debounce((e) => {
            const searchValue = e.target.value;
            setIsLoading(true);
            setFilter((prev) => ({ ...prev, searchTerm: searchValue, skip: 0 }));

            dispatch(
              getPlatformCategory({
                skip: 0,
                limit: filter?.limit,
                platform: platform,
                searchTerm: searchValue || "",
              })
            )
              .unwrap()
              .then((res) => {
                setIsLoading(false);
                const { data, total } = res;
                const categoryData = data?.map((item) => {
                  return {
                    categoryId: item?.categoryId,
                    categoryName: item?.categoryName,
                    categorySlug: item?.categorySlug,
                    status: item?.status,
                    status: item?.status,
                  };
                });
                setTotal(total);
                setCategoriesList(categoryData);
              })
              .catch(() => {
                setIsLoading(false);
              });
          }, 500)}
        />
      </Box>

     
      {/* Empty State */}
      {!isLoading && categoriesList?.length === 0 && (
        <Box sx={{ p: 3, transform: "scale(0.9)" }}>
          Data Not Found
        </Box>
      )}
      {Array.isArray(inputValue) && categoriesList?.length > 0 && (
        <ListItem
          disablePadding
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            position: "sticky",
            top: 0,
            bgcolor: "background.paper",
            zIndex: 1,
          }}
        >
          <ListItemButton onClick={handleSelectAll} className="p-0">
            <Checkbox
              checked={isAllSelected}
              indeterminate={inputValue.length > 0 && !isAllSelected}
              tabIndex={-1}
              disableRipple
            />
            <ListItemText primary={<Typography fontWeight={600}>Select All</Typography>} />
          </ListItemButton>
        </ListItem>
      )}

      <Box sx={{ maxHeight: 300, overflow: "auto" }}>
        {categoriesList.map((_, index) => (
          <Row key={categoriesList[index]?.categoryId} index={index} />
        ))}
      </Box>
       {/* Loading State */}
      <>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={24} color="primary" />
          </Box>
        )}{" "}
      </>
    </Box>
  );
}
